import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { DownloadItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/DownloadItemFragmentFragmentDoc';
import { Link } from '@packages/modules/src/Link/Link';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { useIntl } from 'react-intl';
import { downloadItemMessages } from './messages';

/* GraphQL */ `
  fragment DownloadItemFragment on Download {
    link
    type
  }
`;

export type DownloadItemProps = {
  /**
   * Download data from current product.
   */
  data: FragmentType<typeof DownloadItemFragmentFragmentDoc>;
  /**
   * Decides wether or not the download icon should be rendered
   * @default true
   */
  renderIcon?: boolean;
  /**
   * Optional - displays the index as text next to the message.
   */
  idx?: number;
};

/**
 * The Download component is used to display a link with or without an icon.
 * The link is clickable and opens a downloadable file in a new tab.
 * */
export const DownloadItem = ({ data, renderIcon = true, idx }: DownloadItemProps) => {
  const { formatMessage } = useIntl();
  const { link, type } = getFragmentData(DownloadItemFragmentFragmentDoc, data);

  // if there is a new DownloadType, which is missing in messages, we return null
  if (!(type in downloadItemMessages)) {
    return null;
  }

  const style = renderIcon ? 'text-size-body2' : 'text-size-body3 underline';

  return (
    <Link href={link} noLinkStyle openInNewTab className={cn('flex items-center gap-1', style)}>
      {renderIcon && <span className="icon-emp-pdf decor text-size-h3" />}
      {formatMessage(downloadItemMessages[type])} {idx}
    </Link>
  );
};
